/* Little Yarnlings Original. No theme vendor runtime dependencies. */
(() => {
  'use strict';
  class YarnlingPurchase extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const source = this.querySelector('[data-purchase-config]');
      if (!source) return;
      try { this.data = JSON.parse(source.textContent); } catch { return; }
      this.ready = true;
      this.mode = this.data.mode || 'single';
      this.characters = new Map(this.data.variants.map(v => [String(v.id), v]));
      this.chosen = String(this.data.selected || this.data.variants[0]?.id || '');
      this.picks = [];
      this.pending = false;
      this.unknown = false;
      this.money = new Intl.NumberFormat(this.data.locale || 'en', { style: 'currency', currency: this.data.currency || 'USD' });
      this.form = this.querySelector('form');
      this.qty = this.form.querySelector('[name="quantity"]');
      this.addEventListener('click', event => this.clickHandler(event));
      this.addEventListener('change', event => this.changeHandler(event));
      this.addEventListener('input', event => { if (event.target === this.qty) { this.clearError(); this.paint(); } });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.purchase(); });
      window.addEventListener('pageshow', event => { if (event.persisted) window.location.reload(); });
      this.applyMode(this.mode);
      const buy = this.querySelector('[data-buy]');
      if (this.querySelector('[data-sticky]') && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const box = this.querySelector('[data-sticky]');
          if (box) box.hidden = entries[0].isIntersecting || entries[0].boundingClientRect.top > 0;
        });
        this.observer.observe(buy);
      }
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    sku() {
      const base = this.mode === 'single' ? this.characters.get(this.chosen) : this.data.packs[this.mode];
      const plan = this.form.querySelector('[data-plan]')?.value;
      const allocation = (base?.plans || []).find(a => String(a.id) === plan);
      return allocation ? { ...base, price: allocation.price, compare: allocation.compare } : base;
    }
    maximum(sku) {
      if (!sku?.available) return 0;
      let cap = sku.max == null ? Infinity : Number(sku.max);
      if (sku.tracked && !sku.continueSelling) cap = Math.min(cap, Number(sku.stock));
      return cap;
    }
    selection() {
      const sku = this.sku();
      const quantity = Number(this.qty.value);
      const min = Math.max(1, Number(sku?.min || 1));
      const step = Math.max(1, Number(sku?.step || 1));
      const quantityOK = this.qty.value.trim() !== '' && Number.isSafeInteger(quantity) && quantity >= min && quantity <= this.maximum(sku) && (quantity - min) % step === 0;
      const size = this.mode === 'single' ? 0 : Number(this.mode);
      // Pack 8 is one of every character; Pack 6 keeps the customer's own picks.
      const selected = this.mode === '8' ? [...this.characters.keys()] : this.picks.slice(0, size);
      const filled = selected.filter(id => this.characters.has(id)).length;
      const plan = this.form.querySelector('[data-plan]')?.value;
      const planMissing = plan && !(sku?.plans || []).some(a => String(a.id) === plan);
      const needsPlan = (sku?.requiresPlan && !plan) || !!planMissing;
      const valid = !!sku && quantityOK && filled === size && !needsPlan;
      const properties = {};
      if (size && filled === size) selected.forEach((id, i) => { properties['Character ' + (i + 1)] = this.characters.get(id).title; });
      return { sku, size, filled, quantity, valid, needsPlan, item: valid ? { id: sku.id, quantity, ...(size ? { properties } : {}), ...(plan ? { selling_plan: Number(plan) } : {}) } : null };
    }
    amount(cents) { return this.money.format(Number(cents) / 100) + (this.data.showCurrency ? ' ' + this.data.currency : ''); }
    setText(selector, value) { this.querySelectorAll(selector).forEach(el => { el.textContent = value; }); }
    clearError() { if (!this.unknown) { const el = this.querySelector('[data-error]'); el.hidden = true; el.textContent = ''; } }
    error(message) { const el = this.querySelector('[data-error]'); el.textContent = message; el.hidden = false; }
    rules() {
      const sku = this.sku();
      this.qty.min = String(sku?.min || 1);
      this.qty.step = String(sku?.step || 1);
      const max = this.maximum(sku);
      this.qty.max = Number.isFinite(max) ? String(max) : '';
      this.qty.value = String(sku?.min || 1);
    }
    applyMode(mode) {
      if (this.pending || this.unknown) return;
      if (mode !== 'single' && !this.data.packs[mode]) return;
      this.mode = mode;
      this.clearError();
      const single = this.querySelector('[data-single-panel]');
      const pack = this.querySelector('[data-pack-panel]');
      const complete = this.querySelector('[data-complete-panel]');
      if (single) single.hidden = mode !== 'single';
      if (pack) pack.hidden = mode !== '6';
      if (complete) complete.hidden = mode !== '8';
      if (mode === '6') this.drawSlots(6);
      else if (mode === 'single') this.picture(this.characters.get(this.chosen), true);
      this.rules();
      this.paint();
    }
    drawSlots(size) {
      this.picks = Array.from({ length: size }, (_, i) => this.picks[i] || '');
      const host = this.querySelector('[data-slots]');
      host.replaceChildren();
      this.picks.forEach((selected, index) => {
        const slot = document.createElement('div'); slot.className = 'pack-slot';
        const thumbnail = document.createElement('span'); thumbnail.className = 'slot-empty'; thumbnail.dataset.slotThumbnail = String(index); thumbnail.textContent = '?'; thumbnail.setAttribute('aria-hidden', 'true');
        const field = document.createElement('div');
        const label = document.createElement('label');
        const select = document.createElement('select');
        select.id = 'CharacterSlot-' + this.form.id + '-' + index;
        select.dataset.slot = String(index);
        select.name = 'properties[Character ' + (index + 1) + ']';
        select.required = true;
        label.htmlFor = select.id;
        label.textContent = (index + 1) + '. Choose your character';
        select.append(new Option('Choose a character', ''));
        this.characters.forEach(v => { select.append(new Option(v.title, String(v.id))); });
        select.value = selected;
        field.append(label, select); slot.append(thumbnail, field); host.append(slot);
        this.slotPicture(index);
      });
    }
    slotPicture(index) {
      const old = this.querySelector('[data-slot-thumbnail="' + index + '"]');
      const character = this.characters.get(this.picks[index]);
      const next = document.createElement(character?.image ? 'img' : 'span');
      next.dataset.slotThumbnail = String(index); next.setAttribute('aria-hidden', 'true');
      if (character?.image) { next.src = character.image; next.alt = ''; next.className = 'slot-image'; next.width = 44; next.height = 55; }
      else { next.textContent = '?'; next.className = 'slot-empty'; }
      old.replaceWith(next);
    }
    picture(character, updateURL) {
      if (!character) return;
      let img = this.querySelector('.main-photo img');
      if (character.image) {
        if (!img) { img = document.createElement('img'); img.width = 1254; img.height = 1254; this.querySelector('.main-photo').replaceChildren(img); }
        img.src = character.image; img.removeAttribute('srcset'); img.alt = character.title;
      }
      this.querySelectorAll('[data-character-image]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.characterImage === String(character.id))));
      if (updateURL && !this.data.packPage) { const url = new URL(window.location.href); url.searchParams.set('variant', character.id); window.history.replaceState({}, '', url); }
    }
    paint() {
      const order = this.selection();
      const locked = this.pending || this.unknown;
      const unavailable = !order.sku || this.maximum(order.sku) === 0;
      this.setText('[data-price]', order.sku ? this.amount(order.sku.price) : 'Unavailable');
      const reduced = order.sku && order.sku.compare > order.sku.price;
      this.querySelectorAll('[data-compare]').forEach(el => { el.hidden = !reduced; el.textContent = reduced ? (this.data.compactCompare ? this.money.format(order.sku.compare / 100) : this.amount(order.sku.compare)) : ''; });
      this.querySelectorAll('[data-saving]').forEach(el => { el.hidden = !reduced; el.textContent = reduced ? 'SAVE ' + Math.round((1 - order.sku.price / order.sku.compare) * 100) + '%' : ''; });
      const collectionMissing = this.mode === '8' && order.filled !== 8;
      const label = this.pending ? 'Adding…' : this.unknown ? 'Check your cart' : unavailable ? 'Unavailable' : collectionMissing ? 'Collection unavailable' : order.needsPlan ? 'Choose a purchase option' : order.filled < order.size ? 'Choose all ' + order.size + ' characters' : !order.valid ? 'Check quantity' : order.size ? 'Add Pack ' + order.size + ' to cart' : 'Add to cart';
      this.querySelectorAll('[data-buy],[data-sticky-buy]').forEach(el => { el.disabled = locked || !order.valid; el.textContent = label; });
      this.querySelectorAll('select,input').forEach(el => {
        const hiddenChoice = (el.hasAttribute('data-slot') && this.mode !== '6') || (el.hasAttribute('data-single') && this.mode !== 'single');
        el.disabled = locked || hiddenChoice;
        if (el.hasAttribute('data-slot')) el.required = this.mode === '6';
      });
      const planPicker = this.form.querySelector('[data-plan]');
      if (planPicker) [...planPicker.options].forEach(option => {
        option.disabled = option.value ? !(order.sku?.plans || []).some(a => String(a.id) === option.value) : !!order.sku?.requiresPlan;
      });
      this.querySelectorAll('[data-set]').forEach(el => { el.setAttribute('aria-pressed', String(el.dataset.set === this.mode)); el.disabled = locked || (el.dataset.set !== 'single' && !this.data.packs[el.dataset.set]); });
      const summary = order.valid ? (this.mode === '8' ? 'Complete collection (all 8 characters)' : order.size ? 'Pack ' + order.size : order.sku.title) + ' × ' + order.quantity + ' · ' + this.amount(order.sku.price * order.quantity) : collectionMissing ? 'The complete collection needs all 8 characters.' : order.size ? 'Choose ' + order.size + ' little pals for your pack.' : '';
      this.setText('[data-summary],[data-sticky-summary]', summary);
      this.setText('[data-quantity-label]', order.size ? 'Number of packs' : 'Quantity');
      const orderLabel = order.size ? order.size + ' characters · ' + order.quantity + (order.quantity === 1 ? ' pack' : ' packs') : order.quantity + (order.quantity === 1 ? ' character' : ' characters');
      this.setText('[data-order-label]', order.valid ? orderLabel : order.size ? 'Choose your little pals' : 'Check quantity');
      this.setText('[data-order-total]', order.sku ? this.money.format(order.sku.price * order.quantity / 100) : 'Unavailable');
      this.setText('[data-progress]', order.filled + '/' + order.size + ' selected');
      this.qty.setAttribute('aria-invalid', String(!Number.isInteger(order.quantity) || order.quantity < Number(this.qty.min) || order.quantity > this.maximum(order.sku)));
      this.querySelectorAll('[data-quantity-step]').forEach(button => {
        const next = order.quantity + Number(button.dataset.quantityStep) * Number(this.qty.step);
        button.disabled = locked || next < Number(this.qty.min) || next > this.maximum(order.sku);
      });
      const hidden = this.form.querySelector('[data-sku]'); if (hidden) hidden.value = order.sku?.id || '';
      if (unavailable && !this.unknown) this.error('This option is currently unavailable. Please choose another option.');
      else if (collectionMissing && !this.unknown) this.error('The complete collection is not configured with all 8 characters. Please choose Single or Pack 6.');
    }
    clickHandler(event) {
      const button = event.target.closest('button,a[data-media-url]');
      if (!button || !this.contains(button) || button.disabled || this.pending || this.unknown) return;
      if (button.hasAttribute('data-set')) this.applyMode(button.dataset.set);
      if (button.hasAttribute('data-character-image')) {
        const v = this.characters.get(button.dataset.characterImage);
        if (this.mode === 'single') { this.chosen = String(v.id); this.querySelector('[data-single]').value = this.chosen; this.rules(); this.clearError(); }
        this.picture(v, this.mode === 'single'); this.paint();
      }
      if (button.hasAttribute('data-quantity-step')) { this.qty.value = String(Number(this.qty.value) + Number(button.dataset.quantityStep) * Number(this.qty.step)); this.clearError(); this.paint(); }
      if (button.hasAttribute('data-sticky-buy')) this.purchase();
      if (button.hasAttribute('data-media-url')) { event.preventDefault(); this.picture({ image: button.dataset.mediaUrl, title: this.sku()?.title || '' }, false); }
    }
    changeHandler(event) {
      if (this.pending || this.unknown) return;
      const el = event.target;
      this.clearError();
      if (el.hasAttribute('data-single')) { this.chosen = el.value; this.picture(this.sku(), true); this.rules(); }
      if (el.hasAttribute('data-slot')) { const index = Number(el.dataset.slot); this.picks[index] = el.value; this.slotPicture(index); }
      this.paint();
    }
    async purchase() {
      const order = this.selection();
      if (this.pending || this.unknown || !order.valid) return;
      this.pending = true; this.clearError(); this.paint();
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 20000);
      try {
        const response = await fetch(this.data.add, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [order.item] }), signal: abort.signal });
        let result;
        try { result = await response.json(); } catch { this.unknown = true; throw new Error('We could not confirm the result. Please check your cart before trying again.'); }
        if (!response.ok) {
          if (response.status >= 500 || response.status === 408) this.unknown = true;
          throw new Error(this.unknown ? 'We could not confirm the result. Please check your cart before trying again.' : (typeof result.description === 'string' ? result.description : 'This option could not be added. Please check availability and quantity.'));
        }
        this.unknown = true;
        // Keep the confirmed request on this page briefly so Shopify Customer Events can finish dispatching.
        await new Promise(resolve => setTimeout(resolve, 1000));
        window.location.assign(this.data.cart);
      } catch (error) {
        if (error.name === 'AbortError' || error instanceof TypeError) this.unknown = true;
        this.error(this.unknown ? 'We could not confirm the result. Please check your cart before trying again.' : error.message);
        this.querySelector('[data-cart-help]').hidden = !this.unknown;
      } finally { clearTimeout(timer); this.pending = false; this.paint(); }
    }
  }
  if (!customElements.get('yl-purchase')) customElements.define('yl-purchase', YarnlingPurchase);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.mobile-nav[open],.nav-sub[open]').forEach(el => { el.open = false; el.querySelector('summary')?.focus(); });
  });
  document.addEventListener('submit', event => {
    if (event.submitter?.hasAttribute('data-delete-address') && !window.confirm('Delete this address?')) { event.preventDefault(); return; }
    const form = event.target.closest('[data-track-form]');
    if (!form) return;
    event.preventDefault();
    const number = new FormData(form).get('tracking').trim();
    if (number) window.open('https://t.17track.net/en#nums=' + encodeURIComponent(number), '_blank', 'noopener,noreferrer');
  });
  const addressFields = () => document.querySelectorAll('[data-country-select]').forEach(country => {
    if (country.dataset.initialized) return;
    country.dataset.initialized = 'true';
    const province = country.closest('form').querySelector('[data-province-select]');
    const label = province.closest('[data-province-label]');
    const preferred = [...country.options].find(o => o.value === country.dataset.default || o.textContent === country.dataset.default);
    if (preferred) country.value = preferred.value;
    const populate = () => {
      let choices = [];
      try { choices = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch { /* No province list for this country. */ }
      province.replaceChildren(...choices.map(([value, text]) => new Option(text, value)));
      label.hidden = choices.length === 0;
      province.disabled = choices.length === 0;
      if (choices.some(([value]) => value === province.dataset.default)) province.value = province.dataset.default;
    };
    country.addEventListener('change', populate); populate();
  });
  addressFields();
  document.addEventListener('shopify:section:load', addressFields);
})();
